import { PlusOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import type { ViewConfigKpiPivot } from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { useBooleanState } from '@salescore/frontend-common'
import { App, Button, Checkbox, Empty, Input, message, Spin } from 'antd'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { kpiPivotPickedPresetNameAtom } from '../../../recoil/navigation/atoms'
import { viewsContextAtom } from '../../../recoil/view/atoms'
import { useViewConfigKpiPivot } from '../../../recoil/view/hooks/useViewConfigKpiPivot'
import { useKpiViews } from '../../../recoil/view/selectors/viewsRelatedSelector'
import { buildNewKpiFormModalProperties } from '../../../rsheet/components/modal/NewKpiFormModal'
import { ViewListItem } from './KpiForm/ListItem/ViewListItem'

export const KpiAdditionForm = ({
  prevKpiId,
  hide,
  view,
}: {
  hide: () => void
  prevKpiId?: string
  view: ViewFieldsFragment
}): ReactElement => {
  const { modal } = App.useApp()
  const currentPreset = useRecoilValue(kpiPivotPickedPresetNameAtom)
  const [config, setConfig] = useViewConfigKpiPivot()
  const showAllPresetsOption = config?.presets !== undefined && config.presets.length >= 2
  const [isVisibleInAllPresets, setIsVisibleInAllPresets] = useState(true)
  const existKpiViewIds = new Set(config?.kpis.map((x) => x.viewId))
  const kpiViews = useKpiViews()
  const candidateViews = kpiViews.filter(
    (x) => !existKpiViewIds.has(x.id) && !x.archived && (view.private ? true : !x.private),
  ) // ゴミ箱のビューは除外、公開設定が一致しないものも除外
  const [searchQuery, setSearchQuery] = useState('')
  const context = useRecoilValue(viewsContextAtom)
  // eslint-disable-next-line sonarjs/deprecation
  const loading = useBooleanState()
  const { updateView } = useRecoilValue(viewsContextAtom)

  const filtered =
    searchQuery === ''
      ? candidateViews
      : candidateViews.filter((view) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          searchQuery.split(/\s/v).every((q) => view.name.includes(q) || view.createdBy?.name.includes(q)),
        )

  const addKpi = async (kpiViewId: string, isVisibleInAllPresets: boolean) => {
    const oldKpis = config?.kpis ?? []
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const previousKpiIndex = Math.max(oldKpis.findIndex((x) => x.viewId === prevKpiId) ?? -1, -1) + 1
    const previousKpi = oldKpis[previousKpiIndex]
    const newKpi: ViewConfigKpiPivot['kpis'][0] = {
      viewId: kpiViewId,
      kpiGroupName: previousKpi?.kpiGroupName,
    }
    const kpis = Array.dedupeWith(
      [...oldKpis.slice(0, previousKpiIndex), newKpi, ...oldKpis.slice(previousKpiIndex)],
      (a, b) => a.viewId === b.viewId,
    )
    const presets =
      isVisibleInAllPresets || currentPreset === undefined
        ? config?.presets
        : config?.presets?.map((preset) =>
            preset.name === currentPreset
              ? preset
              : {
                  ...preset,
                  parameter: {
                    ...preset.parameter,
                    invisibleKpiIds: pipe(
                      preset.parameter.invisibleKpiIds ?? [],
                      Array.append(kpiViewId),
                      Array.dedupe,
                    ),
                  },
                },
          )
    try {
      loading.setTrue()
      await context.onAddResource({ viewIds: [kpiViewId] })

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newConfig = { ...config!, kpis, presets }
      setConfig(newConfig)

      // WORKAROUND:
      // configの変更の保存ロジックは、変更後にhasChangeToViewSchemaAtomをtrueにすることで、
      // それをトリガーにuseEffectが呼ばれてその中でupdateViewを実行するのが一般的。
      // （ここのロジックはuseWatchを参照）
      // しかし、KPIを新規作成するとき、別タブが開いてしまう影響でこのuseEffectが呼ばれず、updateViewが実行されない。
      // そのため、ここで明示的にupdateViewを行う
      // 権限がない場合はここまで辿り着かず、またサーバーサイドでエラーになる想定
      await updateView({
        id: view.id,
        config: newConfig,
      })
    } catch {
      void message.error(t(`エラーが発生しました`))
    } finally {
      loading.setFalse()
    }
    hide()
  }

  if (candidateViews.length === 0) {
    return (
      <Empty
        className="pb-4"
        description={
          <div>
            {t(`追加できるKPIがありません。`)}
            <br />
            {t(`まずはKPIを新規作成してください。`)}
            <br />
            <Button
              type="primary"
              className="mb-2 mt-4"
              icon={<PlusOutlined />}
              onClick={() => {
                const m = modal.confirm(
                  buildNewKpiFormModalProperties({
                    showAllPresetsOption,
                    isVisibleInAllPresets,
                    onFinish: (kpiId, isVisibleInAllPresets) => {
                      void addKpi(kpiId, isVisibleInAllPresets)
                      message.success(t(`ダッシュボードにKPIを追加しました。続いてKPIの設定を行なってください。`))
                      m.destroy()
                    },
                  }),
                )
              }}
            >
              {t(`KPIを新規作成`)}
            </Button>
          </div>
        }
      />
    )
  }

  return (
    <div>
      <div className="mb-2 px-1 font-bold">{t(`ダッシュボードにKPIを追加`)}</div>
      <div className="mb-2">
        {showAllPresetsOption && (
          <Checkbox
            className="my-1"
            onChange={(event) => {
              setIsVisibleInAllPresets(event.target.checked)
            }}
            checked={isVisibleInAllPresets}
          >
            {t('全てのプリセットで表示')}
          </Checkbox>
        )}
        <Input.Search
          placeholder={t(`作成済みKPIから検索...`)}
          className="w-full"
          onSearch={(e) => {
            setSearchQuery(e)
          }}
          onChange={(e) => {
            setSearchQuery(e.target.value)
          }}
          onKeyDown={(e) => {
            e.stopPropagation()
          }}
        />
      </div>
      <Spin spinning={loading.isTrue}>
        <div style={{ height: 400, overflowY: 'scroll', borderTop: `1px solid #eee`, borderBottom: `1px solid #eee` }}>
          {filtered.map((view) => (
            <div
              key={view.id}
              className="hover:bg-gray-100"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
              onClick={async () => {
                await addKpi(view.id, isVisibleInAllPresets)
              }}
            >
              <div className={searchQuery === '' ? 'p-1' : 'p-1 pb-2'}>
                <ViewListItem view={view} showPath={searchQuery !== ''} />
              </div>
            </div>
          ))}
        </div>
      </Spin>
      <div className="pt-2">
        <Button
          type="text"
          className="w-full text-blue-500"
          icon={<PlusOutlined />}
          onClick={() => {
            const m = modal.confirm(
              buildNewKpiFormModalProperties({
                showAllPresetsOption,
                isVisibleInAllPresets,
                onFinish: (kpiId, isVisibleInAllPresets) => {
                  void addKpi(kpiId, isVisibleInAllPresets)
                  message.success(t(`ダッシュボードにKPIを追加しました。続いてKPIの設定を行なってください。`))
                  m.destroy()
                },
              }),
            )
          }}
        >
          {t(`KPIを新規作成`)}
        </Button>
      </div>
    </div>
  )
}
